/**
 * Reading a stored `createdBy` / `updatedBy` value.
 *
 * The field holds whatever the writer of the day put there, and the console printed it verbatim.
 * Production sampling of these values found four different things in one column:
 *
 *   - ObjectIds, which are the majority and 94% of which resolve to a real account:
 *     234 employers, 9 job seekers and 2 internal users out of 260 distinct ids
 *   - email addresses, already readable
 *   - system labels: SYSTEM, data-migration-service, system-cleanup
 *   - migration debris, literally `{"User": {"$oid": "..."}, "UpdatedDate": ...}` as a string
 *
 * So a single "look it up" is not enough. This classifies the value first, and only the ids that
 * survive that are worth a round trip.
 */

export type ActorKind = 'system' | 'email' | 'id' | 'text';

export interface ResolvedActor {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

const OBJECT_ID = /^[0-9a-fA-F]{24}$/;

/**
 * Values written by the platform rather than a person, mapped to what they mean.
 *
 * Shown as words because "data-migration-service" in a Created by line reads like a broken
 * lookup, when it is the honest answer: nobody created this, an import did.
 */
const SYSTEM_LABELS: Record<string, string> = {
  SYSTEM: 'System',
  system: 'System',
  'system-cleanup': 'System (cleanup)',
  'data-migration-service': 'Data migration',
};

/**
 * Pull an id out of the migration debris that ended up in some `updatedBy` fields.
 *
 * A handful of job records hold a JSON fragment as their audit string. Rendering that raw is how
 * `{"User": {"$oid": "63dc..."}, "UpdatedDate": 1.69e12}` reached the screen. The id inside it is
 * real and resolves, so it is worth digging out rather than discarding.
 */
const extractEmbeddedId = (raw: string): string | null => {
  const m = /"\$oid"\s*:\s*"([0-9a-fA-F]{24})"/.exec(raw);
  return m ? m[1] : null;
};

export interface ClassifiedActor {
  kind: ActorKind;
  /** What to show before any lookup. */
  fallbackLabel: string;
  /** Set only when kind is 'id' — the value worth resolving. */
  lookupId?: string;
}

/** What kind of thing this audit value is, and what to show if nothing resolves it. */
export function classifyActor(rawValue: string | null | undefined): ClassifiedActor | null {
  const raw = (rawValue ?? '').trim();
  if (!raw) return null;

  if (SYSTEM_LABELS[raw]) {
    return { kind: 'system', fallbackLabel: SYSTEM_LABELS[raw] };
  }
  if (OBJECT_ID.test(raw)) {
    return { kind: 'id', fallbackLabel: shortenId(raw), lookupId: raw };
  }
  if (raw.includes('@')) {
    return { kind: 'email', fallbackLabel: raw };
  }
  const embedded = extractEmbeddedId(raw);
  if (embedded) {
    return { kind: 'id', fallbackLabel: shortenId(embedded), lookupId: embedded };
  }
  return { kind: 'text', fallbackLabel: raw };
}

/**
 * An unresolvable id, shortened.
 *
 * Some ids genuinely resolve to nothing -- every `jobs.createdBy` value sampled was orphaned,
 * left behind by an earlier system. A full 24-character blob reads as a bug; a short one reads as
 * a reference, which is what it is.
 */
export function shortenId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

/** Role codes as a person would say them. */
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  HR: 'HR',
  HR_LEAD: 'HR Lead',
  MARKETING: 'Marketing',
  EMPLOYER: 'Employer',
  JOB_SEEKER: 'Job Seeker',
  ADMIN: 'Super Admin',
  VIEWER: 'Super Admin',
  admin: 'Super Admin',
};

export function roleLabel(role?: string | null): string | null {
  if (!role) return null;
  return ROLE_LABELS[role] ?? role;
}

/**
 * The final display text for one audit value.
 *
 * Name first, then email, then whatever the classification could offer. The raw id is genuinely
 * last: it is a reference nobody can read, and showing it before an email we already hold is the
 * behaviour being fixed.
 */
export function actorLabel(
  classified: ClassifiedActor,
  resolved?: ResolvedActor | null,
): string {
  return resolved?.name || resolved?.email || classified.fallbackLabel;
}
